// src/services/apiClient.ts
import axios, { AxiosError } from "axios";

export const BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false, // allow cookies (for screen_unlocked)
});

// Storage keys – keep consistent everywhere
const ACCESS_KEY = "access_token";
const REFRESH_KEY = "refreshToken";
// Active business; see services/workspaceApi.ts
const WORKSPACE_KEY = "active_workspace_id";

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    const workspaceId = localStorage.getItem(WORKSPACE_KEY);
    if (workspaceId && config.headers?.["X-Workspace-Id"] === undefined) {
      config.headers = config.headers || {};
      config.headers["X-Workspace-Id"] = workspaceId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let queue: Array<(t: string | null) => void> = [];

function onRefreshed(token: string | null) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config!;
    const status = error.response?.status ?? 0;

    // No longer allowed in the selected business (e.g. removed as an
    // employee): drop it and reload into the default business.
    if (error.response?.headers?.["x-workspace-error"]) {
      localStorage.removeItem(WORKSPACE_KEY);
      window.location.reload();
      return Promise.reject(error);
    }
    const requestUrl = String(original?.url || "");
    // A wrong password/passcode answers 401 too.
    const isPasscodeRoute =
      requestUrl.includes("/auth/passcode") ||
      requestUrl.includes("/auth/change-password");
    const isPublicAuthRoute = [
      "/auth/login",
      "/auth/create",
      "/auth/forgot-password",
      "/auth/reset-password",
      "/auth/verify-email",
      "/auth/resend-verification",
    ].some((route) => requestUrl.includes(route));

    // Expected credential errors must reach their form instead of being
    // mistaken for an expired authenticated session.
    if (status === 401 && (isPasscodeRoute || isPublicAuthRoute)) {
      return Promise.reject(error);
    }

    if (status === 401 && !original._retry) {
      original._retry = true;

      if (isRefreshing) {
        // wait for a single refresh to finish
        return new Promise((resolve, reject) => {
          queue.push((newToken) => {
            if (!newToken) return reject(error);
            original.headers = original.headers || {};
            (original.headers as any).Authorization = `Bearer ${newToken}`;
            resolve(apiClient(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (!refreshToken) throw new Error("No refresh token");

        const res = await axios.post(
          `${BASE_URL}/auth/refresh-token`,
          { refreshToken },
          { withCredentials: true }
        );

        const newAccessToken = (res.data as any)?.accessToken;
        if (!newAccessToken) throw new Error("No access token in refresh");

        localStorage.setItem(ACCESS_KEY, newAccessToken);

        onRefreshed(newAccessToken);
        original.headers = original.headers || {};
        (original.headers as any).Authorization = `Bearer ${newAccessToken}`;
        return apiClient(original);
      } catch (e) {
        onRefreshed(null);
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
